import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Truck,
  Package,
  MapPin,
  Clock,
  Phone,
  User,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Eye,
  Navigation,
} from 'lucide-react';
import axios from 'axios';
import { DeliveryTimeline } from './DeliveryIntegration';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-blue-100 text-blue-700',
  pickup_assigned: 'bg-purple-100 text-purple-700',
  out_for_pickup: 'bg-indigo-100 text-indigo-700',
  picked_up: 'bg-cyan-100 text-cyan-700',
  at_laundry: 'bg-teal-100 text-teal-700',
  processing: 'bg-orange-100 text-orange-700',
  ready: 'bg-lime-100 text-lime-700',
  delivery_assigned: 'bg-purple-100 text-purple-700',
  out_for_delivery: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const DELIVERY_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'pickup_assigned', label: 'Pickup Assigned' },
  { value: 'out_for_pickup', label: 'Out for Pickup' },
  { value: 'picked_up', label: 'Picked Up' },
  { value: 'at_laundry', label: 'At Laundry' },
  { value: 'processing', label: 'Processing' },
  { value: 'ready', label: 'Ready' },
  { value: 'delivery_assigned', label: 'Delivery Assigned' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Order Details Dialog
const OrderDetailsDialog = ({ order, onStatusUpdate, onAssignDriver }) => {
  const [newStatus, setNewStatus] = useState(order?.status || '');
  const [notes, setNotes] = useState('');
  const [driverName, setDriverName] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [updating, setUpdating] = useState(false);

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    setUpdating(true);
    try {
      await onStatusUpdate(order.id, newStatus, notes);
      setNotes('');
    } finally {
      setUpdating(false);
    }
  };

  const handleAssignDriver = async (type) => {
    if (!driverName || !driverPhone) return;
    setUpdating(true);
    try {
      await onAssignDriver(order.id, driverName, driverPhone, type, vehicleNumber);
      setDriverName('');
      setDriverPhone('');
      setVehicleNumber('');
    } finally {
      setUpdating(false);
    }
  };

  if (!order) return null;

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Order: {order.id}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-6">
        {/* Status Badge */}
        <div className="flex items-center gap-3">
          <Badge className={STATUS_COLORS[order.status] || 'bg-gray-100'}>
            {order.status?.replace(/_/g, ' ').toUpperCase()}
          </Badge>
          <span className="text-sm text-gray-500">
            Created: {new Date(order.created_at).toLocaleString()}
          </span>
        </div>

        {/* Customer Info */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Customer Details</CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                <span>{order.pickup_address?.contact_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-400" />
                <span>{order.pickup_address?.contact_phone}</span>
              </div>
              <div className="col-span-2 flex items-start gap-2">
                <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                <span>
                  {order.pickup_address?.address_line1}
                  {order.pickup_address?.address_line2 && `, ${order.pickup_address.address_line2}`}
                  , {order.pickup_address?.city}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Schedule Info */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Schedule</CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Pickup</p>
                <p className="font-medium">
                  {order.preferred_pickup_date} - {order.preferred_pickup_time_slot?.replace('_', ' ')}
                </p>
                {order.actual_pickup_time && (
                  <p className="text-green-600 text-xs">
                    Picked up: {new Date(order.actual_pickup_time).toLocaleString()}
                  </p>
                )}
              </div>
              <div>
                <p className="text-gray-500">Delivery</p>
                <p className="font-medium">
                  {order.preferred_delivery_date || 'TBD'} - {order.preferred_delivery_time_slot?.replace('_', ' ') || 'TBD'}
                </p>
                {order.actual_delivery_time && (
                  <p className="text-green-600 text-xs">
                    Delivered: {new Date(order.actual_delivery_time).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Driver Info */}
        {(order.pickup_driver || order.delivery_driver) && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Assigned Drivers</CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {order.pickup_driver && (
                  <div>
                    <p className="text-gray-500">Pickup Driver</p>
                    <p className="font-medium">{order.pickup_driver.name}</p>
                    <p className="text-gray-600">{order.pickup_driver.phone}</p>
                    {order.pickup_driver.vehicle_number && (
                      <p className="text-xs text-gray-500">{order.pickup_driver.vehicle_number}</p>
                    )}
                  </div>
                )}
                {order.delivery_driver && (
                  <div>
                    <p className="text-gray-500">Delivery Driver</p>
                    <p className="font-medium">{order.delivery_driver.name}</p>
                    <p className="text-gray-600">{order.delivery_driver.phone}</p>
                    {order.delivery_driver.vehicle_number && (
                      <p className="text-xs text-gray-500">{order.delivery_driver.vehicle_number}</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Items */}
        {order.items_description && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Items</CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <p className="text-sm">{order.items_description}</p>
            </CardContent>
          </Card>
        )}

        {/* Status Timeline */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Status History</CardTitle>
          </CardHeader>
          <CardContent className="py-2 max-h-48 overflow-y-auto">
            {order.status_history?.map((entry, i) => (
              <div key={i} className="flex items-start gap-3 mb-2 text-sm">
                <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5" />
                <div>
                  <p className="font-medium">{entry.status?.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(entry.timestamp).toLocaleString()}
                  </p>
                  {entry.notes && (
                    <p className="text-xs text-gray-600">{entry.notes}</p>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Update Status */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Update Status</CardTitle>
          </CardHeader>
          <CardContent className="py-2 space-y-3">
            <div className="flex gap-2">
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  {DELIVERY_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                onClick={handleUpdateStatus} 
                disabled={updating || !newStatus}
                className="bg-green-600 hover:bg-green-700"
              >
                {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update'}
              </Button>
            </div>
            <Textarea
              placeholder="Add notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </CardContent>
        </Card>

        {/* Assign Driver */}
        {!order.pickup_driver || !order.delivery_driver ? (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Assign Driver</CardTitle>
            </CardHeader>
            <CardContent className="py-2 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <Input
                  placeholder="Driver name"
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                />
                <Input
                  placeholder="Phone"
                  value={driverPhone}
                  onChange={(e) => setDriverPhone(e.target.value)}
                />
                <Input
                  placeholder="Vehicle #"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                {!order.pickup_driver && (
                  <Button
                    variant="outline"
                    onClick={() => handleAssignDriver('pickup')}
                    disabled={updating || !driverName || !driverPhone}
                    className="flex-1"
                  >
                    Assign for Pickup
                  </Button>
                )}
                {!order.delivery_driver && (
                  <Button
                    variant="outline"
                    onClick={() => handleAssignDriver('delivery')}
                    disabled={updating || !driverName || !driverPhone}
                    className="flex-1"
                  >
                    Assign for Delivery
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </DialogContent>
  );
};

// Main Delivery Management Component
const ProviderDeliveryManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '50' });
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      const response = await axios.get(`${API_URL}/api/delivery/orders?${params}`);
      setOrders(response.data.orders || []);
    } catch (err) {
      console.error('Failed to fetch delivery orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, status, notes) => {
    try {
      await axios.put(`${API_URL}/api/delivery/orders/${orderId}/status`, {
        status,
        notes,
        updated_by: 'provider',
      });
      fetchOrders();
      // Update selected order if viewing
      if (selectedOrder?.id === orderId) {
        const response = await axios.get(`${API_URL}/api/delivery/orders/${orderId}`);
        setSelectedOrder(response.data);
      }
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update status');
    }
  };

  const handleAssignDriver = async (orderId, name, phone, type, vehicleNumber) => {
    try {
      await axios.post(`${API_URL}/api/delivery/orders/${orderId}/assign-driver`, null, {
        params: {
          driver_name: name,
          driver_phone: phone,
          driver_type: type,
          vehicle_number: vehicleNumber || undefined,
        },
      });
      fetchOrders();
      // Update selected order
      if (selectedOrder?.id === orderId) {
        const response = await axios.get(`${API_URL}/api/delivery/orders/${orderId}`);
        setSelectedOrder(response.data);
      }
    } catch (err) {
      console.error('Failed to assign driver:', err);
      alert('Failed to assign driver');
    }
  };

  const viewOrder = async (order) => {
    try {
      const response = await axios.get(`${API_URL}/api/delivery/orders/${order.id}`);
      setSelectedOrder(response.data);
      setDialogOpen(true);
    } catch (err) {
      console.error('Failed to fetch order details:', err);
    }
  };

  // Stats
  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    inProgress: orders.filter(o => ['confirmed', 'pickup_assigned', 'out_for_pickup', 'picked_up', 'at_laundry', 'processing', 'ready', 'delivery_assigned', 'out_for_delivery'].includes(o.status)).length,
    delivered: orders.filter(o => o.status === 'delivered').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="h-6 w-6 text-green-600" />
            Delivery Management
          </h2>
          <p className="text-gray-500">Manage pickup and delivery orders</p>
        </div>
        <Button onClick={fetchOrders} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Orders</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <p className="text-sm text-yellow-700">Pending</p>
            <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <p className="text-sm text-blue-700">In Progress</p>
            <p className="text-2xl font-bold text-blue-700">{stats.inProgress}</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <p className="text-sm text-green-700">Delivered</p>
            <p className="text-2xl font-bold text-green-700">{stats.delivered}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Orders</SelectItem>
            {DELIVERY_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-green-500" />
              <p className="mt-2 text-gray-500">Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No delivery orders found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Pickup Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="font-mono text-sm">
                      {order.id}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{order.pickup_address?.contact_name}</p>
                        <p className="text-sm text-gray-500">{order.pickup_address?.contact_phone}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p>{order.preferred_pickup_date}</p>
                        <p className="text-sm text-gray-500">
                          {order.preferred_pickup_time_slot?.replace('_', ' ')}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[order.status] || 'bg-gray-100'}>
                        {order.status?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {order.pickup_driver ? (
                        <p className="text-sm">{order.pickup_driver.name}</p>
                      ) : (
                        <span className="text-gray-400 text-sm">Not assigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Dialog open={dialogOpen && selectedOrder?.id === order.id} onOpenChange={(open) => {
                        if (!open) {
                          setDialogOpen(false);
                          setSelectedOrder(null);
                        }
                      }}>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => viewOrder(order)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </DialogTrigger>
                        <OrderDetailsDialog
                          order={selectedOrder}
                          onStatusUpdate={handleStatusUpdate}
                          onAssignDriver={handleAssignDriver}
                        />
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProviderDeliveryManagement;
